import _ from "lodash";
import { createSelector, Selector } from "reselect";
import { ACCOUNT_TYPES, FEATURES } from "../../app/appConstants";
import { getAppFeatures } from "../../app/appSelectors";
import { IFeatures } from "../../app/types/appActions.t";
import { IRootState } from "../../app/types/rootReducers.t";
import parseAccountName from "../../utils/parseAccountName";
import { BONUS_NAMES, PROMOCODE_TYPES } from "./bonusesConstants";
import { IBonusesActive, IBonusesActiveList, IBonusesAvailableList, IPromocodesList } from "./types/bonusesActions";
import { getAccountPortfolio } from "../Account/accountSelectors";
import { IAccountPortfolio } from "../Account/types/accountActions.t";

const moduleSelector = (state: IRootState) => state.bonuses;

export const getBonusesActiveList = createSelector(
    moduleSelector, (bonus): IBonusesActiveList => bonus.bonusesActiveList,
);

export const getBonusesAvailableList = createSelector(
    moduleSelector, bonus => bonus.bonusesAvailableList,
);

export const getPromocodesActive = createSelector(
    getBonusesActiveList, list => _.filter(list, { 'is_promocode': true }),
);

export const getBonusAvailableNewLifeLiveAccount = createSelector(
    getBonusesAvailableList, (list: IBonusesAvailableList) => _.find(list, (item) =>
        item.bonus_name === BONUS_NAMES.NEW_LIFE && parseAccountName(item.account)?.type === ACCOUNT_TYPES.LIVE)
);

export const getBonusActiveNewLifeLiveAccount = createSelector(
    getBonusesActiveList, list => _.find(list, (item) =>
        item.name === BONUS_NAMES.NEW_LIFE && parseAccountName(item.account)?.type === ACCOUNT_TYPES.LIVE)
);

export const getIsRespawnAvailable = createSelector(
    [getAppFeatures, getBonusActiveNewLifeLiveAccount, getBonusAvailableNewLifeLiveAccount],
    (features: IFeatures, bonusActiveNewLifeLiveAccount, bonusAvailableNewLifeLiveAccount) =>
        !!features?.[FEATURES.NEW_LIFE] && (!!bonusActiveNewLifeLiveAccount || !!bonusAvailableNewLifeLiveAccount)
);

export const getNewLifeAccounts = createSelector(
    moduleSelector, state => state.newLifeAccounts,
);

export const getNewLifeAccountsError = createSelector(
    moduleSelector, state => state.newLifeAccountsError,
);

export const getNewLifeAccountsIsLoading = createSelector(
    moduleSelector, state => state.newLifeAccountsIsLoading,
);

export const getNewLifeRespawnError = createSelector(
    moduleSelector, state => state.newLifeRespawnError,
);

export const getNewLifeRespawnInProgress = createSelector(
    moduleSelector, state => state.newLifeRespawnInProgress,
);

export const getNewLifeRespawnSuccess = createSelector(
    moduleSelector, state => state.newLifeRespawnSuccess,
);

export const getWelcomeBonusActive = createSelector(
    getBonusesActiveList, state => _.find(state, { name: BONUS_NAMES.WELCOME })
);

export const getWelcomeBonusAvailable = createSelector(
    getBonusesAvailableList, state => _.find(state, { bonus_name: BONUS_NAMES.WELCOME })
);

export const getBonusDepositActive = createSelector(
    getBonusesActiveList, state => _.find(state, { name: BONUS_NAMES.DEPOSIT })
);

export const getWelcomeApplyBonus = createSelector(moduleSelector, state => state.welcomeApplyBonus);
export const getWelcomeBannerOpened = createSelector(moduleSelector, state => state.welcomeBannerOpened);

export const getNewLifeRespawnBannerOpened = createSelector(moduleSelector, state => state.newLifeRespawnBannerOpened);

export const getBonusPromocode = createSelector(moduleSelector, state => state.promocode);
export const getBonusPromocodeError = createSelector(moduleSelector, state => state.promocodeError);
export const getBonusPromocodeIsLoading = createSelector(moduleSelector, state => state.promocodeIsLoading);

export const getActivePromocodes = createSelector(moduleSelector, state => _.reverse(_.sortBy(state.activePromocodes, 'id')));
export const getActivePromocodesError = createSelector(moduleSelector, state => state.activePromocodesError);
export const getActivePromocodesIsLoading = createSelector(moduleSelector, state => state.activePromocodesIsLoading);

export const getUsedPromocodes = createSelector(moduleSelector, state => _.reverse(_.sortBy(state.usedPromocodes, 'id')));

export const getWelcomePromocode = createSelector(
    getActivePromocodes, state => _.find(state, { promocode: 'welcome' })
);

export const hasWelcomePromocode = createSelector(
    [getWelcomePromocode], (welcomePromocode) => !!welcomePromocode
);

export const getActivatedBonusesToShowUser = createSelector<[Selector<IRootState, IBonusesActiveList>, Selector<IRootState, string>], IBonusesActiveList>(
    [getBonusesActiveList, (state, accountCode) => accountCode], (bonusesActiveList, accountCode) =>
        _.filter(bonusesActiveList, (bonus) =>
            (bonus.name === BONUS_NAMES.WELCOME) && (!accountCode || bonus.account === accountCode))
);

export const getUsedPromocodesToShowUser = createSelector<[Selector<IRootState, IPromocodesList>, Selector<IRootState, string>], IPromocodesList>(
    [getUsedPromocodes, (state, accountCode) => accountCode], (usedPromocodes, accountCode) =>
        _.filter(usedPromocodes, (promocode) =>
            (promocode.type === PROMOCODE_TYPES.NODEPOSIT && +promocode.credit > 0) && (!accountCode || promocode.account === accountCode))
);

export const getRespawnBannerPortfolioOpened = createSelector(moduleSelector, state => state.respawnBannerPortfolioOpened);
export const getRespawnBannerProfileOpened = createSelector(moduleSelector, state => state.respawnBannerProfileOpened);

export const getWithdrawalBonusAmount = createSelector<[Selector<IRootState, IBonusesActive>, Selector<IRootState, IAccountPortfolio | Partial<IAccountPortfolio>>, Selector<IRootState, number>], number>(
        [getWelcomeBonusActive, getAccountPortfolio, (state, withdrawAmount) => withdrawAmount],
        (bonusActive, portfolio, withdrawAmount) => {
            if (bonusActive) {
                const preliminaryBonusAmount = Math.max(withdrawAmount, withdrawAmount * bonusActive.credit / portfolio.balance);
                return bonusActive?.credit < preliminaryBonusAmount ? bonusActive?.credit : preliminaryBonusAmount;
            }
            return 0;
        }
);
